import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Upsell, UpsellFromError } from '../../components/plan/Upsell';
import { usePlan } from '../../components/plan/usePlan';
import { platformLabel } from '../../lib/platforms';
import { safeExternalUrl } from '../../lib/url';
import type {
  GenerationRequestDetailRead,
  PlatformConnectionRead,
  PublishJobStatus,
} from '../../types/api';
import { knowledgeBaseSectionPath } from '../knowledge-base/sections';
import { takePostAfterApprove } from './postAfterApprove';
import { usePublishJob } from './usePublishJob';

/** Where every "you need a connection" link on this control points. */
const INTEGRATIONS_PATH = knowledgeBaseSectionPath('integrations');

/** What the worker is doing, in its own words. `complete` names the platform. */
const STAGE_LABEL: Readonly<Record<Exclude<PublishJobStatus, 'complete'>, string>> = {
  pending: 'Queued',
  uploading: 'Uploading images',
  posting: 'Posting',
  failed: 'That post did not go out',
};

interface PublishButtonProps {
  businessId: string;
  request: GenerationRequestDetailRead;
  /** The request platform's row from `useConnections`. Null while it is still loading. */
  connection: PlatformConnectionRead | null;
  /** Re-read the request: the published post and its permalink live on it. */
  onPublished: () => void;
}

/**
 * Publish this finished post to LinkedIn or a Facebook Page.
 *
 * The one place in the product where content leaves for a platform rather than
 * for the user's own hands, so it says what it is about to do and asks once:
 * the post goes out publicly, immediately, and nothing on this screen can take
 * it back afterwards.
 *
 * It never renders for a request written for another platform — the caller
 * decides that — and it renders as a link rather than a button when there is no
 * usable connection, because the way forward then is the Connected accounts tab,
 * not a disabled control the user can only guess at.
 *
 * Publishing is a worker job, so the button hands off to {@link usePublishJob}
 * and reports the stage it is on. Approving a draft still publishes nothing;
 * this is the separate, explicit step.
 */
export function PublishButton({
  businessId,
  request,
  connection,
  onPublished,
}: PublishButtonProps) {
  const [isConfirming, setIsConfirming] = useState(false);
  const label = platformLabel(request.platform);
  const isFacebook = request.platform === 'facebook';
  const publish = usePublishJob(businessId, request.id, onPublished, label);
  const { can } = usePlan();
  const navigate = useNavigate();
  // Arrived from "Post to {platform}" on the approval step: taken once.
  const [wantsPost, setWantsPost] = useState(() => takePostAfterApprove(request.id));

  const postReady =
    wantsPost &&
    connection !== null &&
    connection.available &&
    request.published_post === null &&
    can('publish');
  const needsConnecting =
    postReady && (connection.status === 'unconnected' || connection.status === 'expired');

  // Not connected yet: straight to Connected accounts.
  useEffect(() => {
    if (needsConnecting) void navigate(INTEGRATIONS_PATH);
  }, [needsConnecting, navigate]);

  // Connected: the confirm opens on arrival rather than waiting for a click.
  const confirming = isConfirming || (postReady && !needsConnecting);
  const closeConfirm = () => {
    setIsConfirming(false);
    setWantsPost(false);
  };

  const published = request.published_post;
  const permalink = safeExternalUrl(published?.external_url ?? null);
  const status = publish.job?.status ?? null;

  // Nothing to offer until we know what the account has, and nothing to offer
  // at all on a deployment with no LinkedIn app configured.
  if (connection === null || !connection.available) return null;

  if (status === 'complete') {
    return (
      <div className="publish-button">
        <p className="publish-button__posted" role="status">
          Posted to {label}
        </p>
        {permalink ? (
          <a href={permalink} target="_blank" rel="noopener noreferrer">
            View post
          </a>
        ) : null}
      </div>
    );
  }

  // Already out — the card carries the Posted stamp and the permalink beside it.
  if (published !== null) return null;

  // Not on this plan: say so where the button would be. A job already running
  // (adopted on mount) still reports below; only starting one is gated.
  if (!can('publish') && status === null) {
    return (
      <div className="publish-button">
        <Upsell feature="publish" />
      </div>
    );
  }

  if (connection.status === 'unconnected') {
    return (
      <Link className="publish-button__link" to={INTEGRATIONS_PATH}>
        {isFacebook ? 'Connect a Facebook Page to publish' : 'Connect LinkedIn to publish'}
      </Link>
    );
  }

  if (connection.status === 'expired') {
    return (
      <Link className="publish-button__link" to={INTEGRATIONS_PATH}>
        Reconnect {label} to publish
      </Link>
    );
  }

  if (status === 'pending' || status === 'uploading' || status === 'posting') {
    return (
      <div className="publish-button">
        <p className="publish-button__status" role="status">
          {STAGE_LABEL[status]}
        </p>
      </div>
    );
  }

  const failure = status === 'failed' ? publish.job : null;

  return (
    <div className="publish-button">
      {confirming ? (
        <div className="publish-button__confirm">
          <p className="publish-button__confirm-message">
            Posts publicly as{' '}
            {connection.author_name ??
              (isFacebook ? 'your Facebook Page' : 'your LinkedIn profile')}{' '}
            now. This cannot be undone from here.
          </p>
          <div className="publish-button__confirm-actions">
            <button
              className="button button--primary"
              type="button"
              disabled={publish.isBusy}
              onClick={() => {
                closeConfirm();
                publish.start();
              }}
            >
              Yes, publish
            </button>
            <button
              className="button"
              type="button"
              disabled={publish.isBusy}
              onClick={closeConfirm}
            >
              Not yet
            </button>
          </div>
        </div>
      ) : (
        <button
          className="button button--primary"
          type="button"
          disabled={publish.isBusy}
          onClick={() => setIsConfirming(true)}
        >
          Publish to {label}
        </button>
      )}

      {failure !== null ? (
        <div className="publish-button__failure">
          <p className="form__error" role="alert">
            {failure.error_detail ?? STAGE_LABEL.failed}
          </p>
          {failure.error_code === 'LINKEDIN_RECONNECT_REQUIRED' ||
          failure.error_code === 'FACEBOOK_RECONNECT_REQUIRED' ? (
            <Link className="publish-button__link" to={INTEGRATIONS_PATH}>
              Reconnect {label}
            </Link>
          ) : null}
        </div>
      ) : null}

      <UpsellFromError error={publish.planError} />

      {publish.error !== null ? (
        <p className="form__error" role="alert">
          {publish.error}
        </p>
      ) : null}
    </div>
  );
}
